import {
  Button,
  HelperText,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Select,
  TextInput,
} from 'flowbite-react';
import { type FormEvent, useState } from 'react';
import {
  useDeleteSecretFieldMutation,
  useSecretFieldsQuery,
  useSetSecretFieldMutation,
} from '../../queries/use-secret-fields.js';

/**
 * Shape of one editable secret field. The whitelist + label / hint /
 * kind data is owned by the SPA — operators see what makes sense to
 * them, regardless of what the backend's allowed-fields list happens
 * to call them. The backend rejects unknown field names regardless.
 */
export interface SecretFieldSpec {
  /** Server-side field key (matches `SECRET_FIELDS` in the backend). */
  key: string;
  /** Operator-facing label. */
  label: string;
  /** Optional inline hint shown beneath the field. */
  hint?: string;
  /** `enum` adds a fixed-set <select>; default is a free-form text input. */
  kind?: 'string' | 'enum';
  enumValues?: readonly string[];
}

interface Props {
  /**
   * API path up to the bundle root, e.g.
   *   `/v1/clients/{cid}` (Client bundle)
   *   `/v1/clients/{cid}/licenses/{lid}` (License bundle)
   *   `/v1/clients/{cid}/oauth-apps/{oid}` (OauthApp bundle)
   *   `/v1/clients/{cid}/workfront-configs/{wfid}` (WorkfrontConfig)
   *   `/v1/clients/{cid}/fusion-configs/{fid}` (FusionConfig)
   * The component appends `/secret-fields` and `/secret-fields/{field}` as needed.
   */
  bundlePath: string;
  /** Allowed-field metadata, in operator-display order. */
  fields: readonly SecretFieldSpec[];
  /** Heading shown above the field list. */
  title: string;
  /** Optional subtitle / scope blurb. */
  subtitle?: string;
}

/**
 * Operator surface for a per-tenant Secrets Manager bundle. Renders one
 * row per editable field — with a Set / Replace / Remove modal that
 * never shows the existing value (Secrets Manager doesn't return
 * values back through this surface — the field is either set or
 * unset, opaque from here).
 *
 * Wired against the secret-storage routes shipped 2026-05-07:
 * `apps/admin-console-api/src/handlers/{entity}-secret-{op}.ts`.
 */
export function SecretBundlePanel({ bundlePath, fields, title, subtitle }: Props): JSX.Element {
  const fieldsQuery = useSecretFieldsQuery(bundlePath);
  const [editing, setEditing] = useState<SecretFieldSpec | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<SecretFieldSpec | null>(null);

  if (fieldsQuery.isLoading) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400" role="status">
        Loading secret fields…
      </p>
    );
  }
  if (fieldsQuery.isError) {
    return (
      <div
        role="alert"
        className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
      >
        Failed to load secret fields:{' '}
        {fieldsQuery.error instanceof Error ? fieldsQuery.error.message : 'unknown error'}
      </div>
    );
  }

  const present = new Set(fieldsQuery.data ?? []);

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <header className="mb-4">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
        {subtitle ? (
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
        ) : null}
      </header>

      <ul className="divide-y divide-gray-200 dark:divide-gray-700">
        {fields.map((spec) => {
          const isSet = present.has(spec.key);
          return (
            <li key={spec.key} className="flex items-start justify-between gap-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {spec.label}
                  </span>
                  {isSet ? (
                    <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-200">
                      Set
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                      Not set
                    </span>
                  )}
                </div>
                {spec.hint ? (
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{spec.hint}</p>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Button size="xs" color="alternative" onClick={() => setEditing(spec)}>
                  {isSet ? 'Replace' : 'Set'}
                </Button>
                {isSet ? (
                  <Button size="xs" color="failure" onClick={() => setConfirmDelete(spec)}>
                    Remove
                  </Button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>

      {editing ? (
        <SecretFieldEditModal
          spec={editing}
          bundlePath={bundlePath}
          isReplacing={present.has(editing.key)}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {confirmDelete ? (
        <SecretFieldDeleteModal
          spec={confirmDelete}
          bundlePath={bundlePath}
          onClose={() => setConfirmDelete(null)}
        />
      ) : null}
    </section>
  );
}

interface EditModalProps {
  spec: SecretFieldSpec;
  bundlePath: string;
  isReplacing: boolean;
  onClose: () => void;
}

function SecretFieldEditModal({
  spec,
  bundlePath,
  isReplacing,
  onClose,
}: EditModalProps): JSX.Element {
  const [value, setValue] = useState('');
  const setMutation = useSetSecretFieldMutation();

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (!value.trim()) return;
    setMutation.mutate({ bundlePath, field: spec.key, value }, { onSuccess: () => onClose() });
  };

  return (
    <Modal show={true} onClose={onClose} size="md" dismissible>
      <ModalHeader>
        {isReplacing ? 'Replace' : 'Set'} {spec.label}
      </ModalHeader>
      <form onSubmit={handleSubmit}>
        <ModalBody className="space-y-3">
          {isReplacing ? (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              The current value is opaque from this surface. Submitting overwrites it; the previous
              value can not be recovered through the UI.
            </p>
          ) : null}
          <div>
            <Label htmlFor="secret-value">{spec.label}</Label>
            {spec.kind === 'enum' && spec.enumValues ? (
              <Select
                id="secret-value"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                disabled={setMutation.isPending}
                required
              >
                <option value="">Select…</option>
                {spec.enumValues.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </Select>
            ) : (
              <TextInput
                id="secret-value"
                type="password"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                disabled={setMutation.isPending}
                autoFocus
                required
              />
            )}
            {spec.hint ? <HelperText className="mt-1">{spec.hint}</HelperText> : null}
          </div>
          {setMutation.isError ? (
            <div
              role="alert"
              className="rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
            >
              {setMutation.error instanceof Error ? setMutation.error.message : 'Save failed'}
            </div>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="gray" onClick={onClose} type="button" disabled={setMutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" disabled={setMutation.isPending || !value.trim()}>
            {setMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

interface DeleteModalProps {
  spec: SecretFieldSpec;
  bundlePath: string;
  onClose: () => void;
}

function SecretFieldDeleteModal({ spec, bundlePath, onClose }: DeleteModalProps): JSX.Element {
  const deleteMutation = useDeleteSecretFieldMutation();

  return (
    <Modal show={true} onClose={onClose} size="md" dismissible>
      <ModalHeader>Remove {spec.label}</ModalHeader>
      <ModalBody className="space-y-3">
        <p className="text-sm text-gray-700 dark:text-gray-200">
          Remove the <code className="font-mono text-xs">{spec.key}</code> field from this bundle?
          The bundle itself is left in place; only this single field is deleted. The action is
          logged to the audit log.
        </p>
        {deleteMutation.isError ? (
          <div
            role="alert"
            className="rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
          >
            {deleteMutation.error instanceof Error ? deleteMutation.error.message : 'Delete failed'}
          </div>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button color="gray" onClick={onClose} type="button" disabled={deleteMutation.isPending}>
          Cancel
        </Button>
        <Button
          color="failure"
          disabled={deleteMutation.isPending}
          onClick={() =>
            deleteMutation.mutate({ bundlePath, field: spec.key }, { onSuccess: () => onClose() })
          }
        >
          {deleteMutation.isPending ? 'Removing…' : 'Remove'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
